import { GoogleOAuthProvider } from '@react-oauth/google'
import {
  QueryClient,
  MutationCache,
  onlineManager,
  useIsRestoring,
  type MutationCacheNotifyEvent,
} from '@tanstack/react-query'
import {
  PersistQueryClientProvider,
  persistQueryClientSave,
  persistQueryClientSubscribe,
  type PersistQueryClientOptions,
} from '@tanstack/react-query-persist-client'
import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import App from '@/App'
import ErrorBoundary from '@/components/layout/ErrorBoundary'
import { ToastProvider, ToastConnector } from '@/components/layout/Toast'
import { initCacheReset } from '@/lib/cacheReset'
import { installGlobalErrorHandlers } from '@/lib/errorReporter'
import { createIDBPersister } from '@/lib/idbPersister'
import { createMutationErrorHandler } from '@/lib/mutationErrorHandler'
import {
  persistMutationsForReplay,
  replayRetryDelay,
  retryReplayable,
  shouldPersistMutation,
  trackReplaySend,
} from '@/lib/mutationReplay'
import { collectReplayableMutations, replayableMutationKeyHashes } from '@/lib/offlineMutations'
import { ApiError } from '@/services/api'
import { onRefusedKeySent } from '@/services/api/idempotencySends'
import { OfflineError } from '@/services/api/OfflineError'
import './index.css'

installGlobalErrorHandlers()

// Mutations that were queued for replay: paused while offline, or restored
// paused from the persisted cache. Lets the 404 handler below distinguish a
// stale queued change (auto-discard) from an interactive 404 (normal error).
const queuedMutations = new WeakSet<object>()

const mutationCache: MutationCache = new MutationCache({
  onError: createMutationErrorHandler(() => mutationCache, queuedMutations),
})

// Track replay-queued mutations. `added` in any state but idle means it was
// restored from persistence (a live mutation is added idle); a `pause`
// action while offline means it was queued by the user. A `pause` while
// online is just scope serialization (waiting on a sibling), not a queue.
// A write's idempotency key is pinned to its user when it is created, and a
// restored one gets its send record back (`lib/mutationReplay.ts`).
type CacheEvent = MutationCacheNotifyEvent

// The write is created, restored, or starts an attempt.
function tracksSend(event: CacheEvent): boolean {
  if (event.type === 'added') return true
  return event.type === 'updated' && (event.action.type === 'pending' || event.action.type === 'continue')
}

// A write failed because the device is offline, which TanStack can't know
// without an `offline` event (none comes on a cold start offline).
function failedOffline(event: CacheEvent): boolean {
  return event.type === 'updated' && event.action.type === 'failed' && event.action.error instanceof OfflineError
}

function isQueued(event: CacheEvent): boolean {
  if (event.type === 'added') return event.mutation.state.status !== 'idle'
  return event.type === 'updated' && event.action.type === 'pause' && !onlineManager.isOnline()
}

// How long a write's offline failure keeps TanStack offline without the
// browser's `online` event. `navigator.onLine` can read false on a working
// network, and then no `online` event ever comes: going back online after
// this long bounds that, and if the device really is offline the next
// attempt fails at once and takes it offline again.
const OFFLINE_RECHECK_MS = 30_000
let offlineRecheck: ReturnType<typeof setTimeout> | undefined

function clearOfflineRecheck() {
  clearTimeout(offlineRecheck)
  offlineRecheck = undefined
}

function markOffline() {
  onlineManager.setOnline(false)
  clearOfflineRecheck()
  offlineRecheck = setTimeout(() => onlineManager.setOnline(true), OFFLINE_RECHECK_MS)
}

// The browser's own word on connectivity supersedes a pending re-check.
window.addEventListener('online', clearOfflineRecheck)
window.addEventListener('offline', clearOfflineRecheck)

mutationCache.subscribe((event) => {
  const { mutation } = event
  if (!mutation) return
  if (tracksSend(event)) trackReplaySend(mutation)
  // Tell TanStack, so retries pause and queue like any offline write and `online` resumes them.
  if (failedOffline(event) && !navigator.onLine) markOffline()
  if (isQueued(event)) queuedMutations.add(mutation)
})

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      // Don't retry client errors (4xx) — they won't succeed on retry and just
      // delay surfacing the result (a not-found shared link otherwise sat on a
      // loading skeleton for ~7.6s while the 404 was retried 3×). Network
      // failures (status 0), timeouts (408) and rate-limits (429) are transient,
      // so keep retrying those plus 5xx, up to 3 times.
      retry: (failureCount, error) => {
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 408 &&
          error.status !== 429
        ) {
          return false
        }
        return failureCount < 3
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
      gcTime: Infinity,
    },
    mutations: {
      // 'online' (not 'offlineFirst'): when offline, mutations pause instead of
      // firing-and-failing. pausedCount drives the OfflineBanner "N changes
      // will sync when you reconnect" message, and there's no spurious
      // FailedMutationsSheet for what is really a queued-for-replay mutation.
      // Paused mutations are persisted via PersistQueryClientProvider and
      // auto-resume when the browser fires the 'online' event (same session)
      // or via the provider's onSuccess restore hook (after a reload).
      networkMode: 'online',
      gcTime: Infinity,
    },
  },
})

// Register mutation defaults for offline replay. A mutation replayed after a
// reload runs with ONLY these defaults (the originating hook's callbacks are
// gone), so this must cover everything replay needs: the mutationFn, the
// serialization scope, and the cache keys to reconcile on settle.
//
// The replayable defs are AUTO-COLLECTED from the hooks layer (see
// `lib/offlineMutations.ts`) — every def returned already has
// `offlineReplay: true`, so declaring that flag is sufficient; no manual
// registration list to keep in sync (issue #116). The wiring below
// (setMutationDefaults + the replayableKeyHashes gate consumed by
// `shouldDehydrateMutation`) is unchanged.
const allMutationDefs = collectReplayableMutations()
const replayableKeyHashes = replayableMutationKeyHashes()
for (const def of allMutationDefs) {
  queryClient.setMutationDefaults(def.mutationKey, {
    mutationFn: def.mutationFn as (...args: unknown[]) => Promise<unknown>,
    scope: def.scope,
    // Retry a session that couldn't be renewed (#254) and transient failures
    // (#258) with backoff; the retryer pauses while hidden or offline, so the
    // write stays queued. Safe because every replayable request carries an
    // idempotency key. Replayable writes only — anything else fails through
    // the normal error path.
    retry: retryReplayable,
    retryDelay: replayRetryDelay,
    onSettled: () => {
      for (const key of def.settledInvalidation ?? []) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })
}

const persister = persistMutationsForReplay(createIDBPersister())
initCacheReset(queryClient, persister)

const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  maxAge: Infinity,
  // Drop the persisted cache whenever a new build ships, so schema-changing
  // deploys can't resurrect stale data (maxAge alone never expires it).
  buster: __BUILD_ID__,
  dehydrateOptions: {
    // Persist replayable writes that haven't landed — queued, in flight,
    // backing off, or failed transiently — so closing the app can't lose
    // them (see `lib/mutationReplay.ts`). Anything else (deck/binder CRUD, filters, …)
    // would hydrate without a mutationFn and become a permanently broken entry.
    shouldDehydrateMutation: (mutation) => shouldPersistMutation(mutation, replayableKeyHashes),
  },
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element not found')

// The restore hook's own save subscription (see `onSuccess` below), released
// once the provider's subscription takes over.
let releaseLaunchSaves: (() => void) | undefined

// A refused write sent again by a backoff retry gets no cache event before the
// request leaves, so save it as possibly sent here — once restore is done, so
// a save can never overwrite the persisted cache before it has been read.
let restored = false
onRefusedKeySent(() => {
  if (restored) void persistQueryClientSave({ queryClient, ...persistOptions })
})

// Rendered inside the provider: its effect runs in the same commit as the
// provider's subscribe effect (children's first), with no cache event between.
// eslint-disable-next-line react-refresh/only-export-components -- entry module, see AppTree
function LaunchSavesHandoff() {
  const isRestoring = useIsRestoring()
  useEffect(() => {
    if (isRestoring) return
    releaseLaunchSaves?.()
    releaseLaunchSaves = undefined
  }, [isRestoring])
  return null
}

// eslint-disable-next-line react-refresh/only-export-components -- entry module: it renders the root and exports nothing, so a full reload on change is the correct (and only possible) HMR behavior
function AppTree() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={() => {
        // Restore finished. If the app reopened while already online there is
        // no 'online' event to kick the queue, so resume explicitly, then
        // refresh queries once the replays have settled. Writes in flight at
        // close were persisted paused (`lib/mutationReplay.ts`), so they resume
        // here too. The provider only starts saving on the render after this
        // hook returns, so save on every cache event until it does: each write
        // the replays send is then recorded as possibly sent before a kill
        // could lose that, and re-send it past the server's idempotency window.
        restored = true
        releaseLaunchSaves = persistQueryClientSubscribe({ queryClient, ...persistOptions })
        queryClient.resumePausedMutations().then(() => queryClient.invalidateQueries())
      }}
      onError={() => {
        // Restore failed and the persisted cache was removed: nothing left unread to protect.
        restored = true
      }}
    >
      <LaunchSavesHandoff />
      <ErrorBoundary>
        <ToastProvider>
          <ToastConnector />
          <App />
        </ToastProvider>
      </ErrorBoundary>
    </PersistQueryClientProvider>
  )
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    {googleClientId ? (
      <GoogleOAuthProvider clientId={googleClientId}>
        <AppTree />
      </GoogleOAuthProvider>
    ) : (
      <AppTree />
    )}
  </React.StrictMode>,
)
